import { useState } from "react";
import { CheckCircle, XCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";

/**
 * Google 連携完了後のリダイレクト先 /link-google/success
 * パスワードを設定するとログイン画面からログインできるようになる。
 */
export function LinkGoogleSuccessPage() {
  const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const done = params.get("done") === "1";
  const error = params.get("error") ?? null;
  const token = params.get("token") ?? null;
  const success = done && !error;

  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [setPasswordError, setSetPasswordError] = useState<string | null>(null);
  const [setPasswordDone, setSetPasswordDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    if (password !== passwordConfirm) {
      setSetPasswordError("パスワードが一致しません");
      return;
    }
    if (password.length < 8) {
      setSetPasswordError("パスワードは8文字以上にしてください");
      return;
    }
    setSubmitting(true);
    setSetPasswordError(null);
    try {
      await api.auth.setPassword({ inviteToken: token, password });
      setSetPasswordDone(true);
    } catch (err) {
      setSetPasswordError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-md border-border/80 bg-card/50 rounded-xl shadow-sm">
        <CardHeader className="text-center pb-2">
          {success ? (
            <div className="flex justify-center">
              <CheckCircle className="size-14 text-emerald-500" aria-hidden />
            </div>
          ) : (
            <div className="flex justify-center">
              <XCircle className="size-14 text-destructive" aria-hidden />
            </div>
          )}
          <CardTitle className="text-xl mt-2">
            {success ? "完了しました" : "連携に失敗しました"}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center pt-0 space-y-4">
          {success ? (
            <>
              <p className="text-muted-foreground text-sm">
                Google 連携が完了しました。カレンダーとメールの連携が有効になりました。
              </p>
              {token && !setPasswordDone ? (
                <div className="text-left pt-4 border-t border-border/60">
                  <p className="text-sm text-muted-foreground mb-3">
                    ログインするにはパスワードを設定してください（8文字以上）。設定後、ログイン画面からメールアドレスとパスワードでログインできます。
                  </p>
                  <form onSubmit={handleSetPassword} className="space-y-3">
                    <div>
                      <Label htmlFor="set-password">パスワード</Label>
                      <Input
                        id="set-password"
                        type="password"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        className="rounded-lg mt-1"
                        minLength={8}
                        autoComplete="new-password"
                      />
                    </div>
                    <div>
                      <Label htmlFor="set-password-confirm">パスワード（確認）</Label>
                      <Input
                        id="set-password-confirm"
                        type="password"
                        value={passwordConfirm}
                        onChange={e => setPasswordConfirm(e.target.value)}
                        className="rounded-lg mt-1"
                        minLength={8}
                        autoComplete="new-password"
                      />
                    </div>
                    {setPasswordError && (
                      <p className="text-sm text-destructive">{setPasswordError}</p>
                    )}
                    <Button type="submit" className="w-full rounded-lg" disabled={submitting}>
                      {submitting ? "設定中…" : "パスワードを設定する"}
                    </Button>
                  </form>
                </div>
              ) : setPasswordDone ? (
                <p className="text-sm text-muted-foreground pt-2">
                  パスワードを設定しました。
                  <br />
                  <a href="/" className="text-primary underline hover:no-underline">
                    ログイン画面
                  </a>
                  からログインできます。
                </p>
              ) : (
                <p className="text-muted-foreground text-sm">このウィンドウを閉じてかまいません。</p>
              )}
            </>
          ) : (
            <p className="text-muted-foreground text-sm">{error ?? "不明なエラー"}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
